import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import DashboardContainer from 'containers/DashboardContainer'
import { flyerDesignLayoutConfig } from 'assets/frontend-json/flyer-position'
import FlyerCard from 'src/containers/event/CreateEvent/SelectFlyerStep/FlyerCard/FlyerCard'
import CustomFlyerPicker from 'src/components/common/CustomFlyerPicker'
import {
  changeEventForm,
  fetchEvent,
  fetchCreateEvent,
  fetchUploadFlyer,
  resetEventForm,
  createTicketType,
  archiveTicketType,
  fetchUploadVideoFlyer,
  fetchZazzleEventInfo
} from 'src/actions/event'
import { eventTicketInfoRequest, setTicketTypes } from 'src/actions/paymentGraphql'
import { fetchFlyerDesigns, setCustomFlyerURL } from 'src/actions/flyer'
import { showLogin } from 'src/actions/login'
import {
  ticketTypesSelector,
  userIdSelector,
  eventFormSelector,
  flyerSelector,
  eventSelector
} from 'src/selectors'
import PreviewIcon from 'assets/icons/preview-icon.png'
import ImagesIcon from 'src/assets/icons/icon_images.png'
import SelectFlyerStep from 'src/containers/event/CreateEvent/SelectFlyerStep/SelectFlyerStep'
import InputOptions from '../InputEventDetail/InputOptions/InputOptions'
import LoadingComponent from 'components/common/LoadingComponent/LoadingComponent'
import LoadingScreen from 'components/LoadingScreen'
import { deepCopy, generate16to9ImageWithBlur } from 'services/utils'
import moment from 'moment-timezone'
import uuid from 'uuid'
import cn from 'classnames'
import { message } from 'antd'
import { redirectDomain } from 'services/url_helpers'
import { isMobile } from 'services/browser_detection'
import BackIcon from 'src/assets/icons/back_button.png'
import SvgIcon from 'src/components/common/SvgIcon'
import SearchIcon from 'src/assets/icons/ico-search-outlined.svg'
import styles from './edit-event.scss'
import ImageSearchModal from '../CreateEvent/ImageSearchModal/ImageSearchModal'
import VideoFlyerCard from '../CreateEvent/SelectFlyerStep/VideoFlyerCard/VideoFlyerCard'
import Button from 'components/common/Button'
import refreshIcon from 'src/assets/icons/ico-refresh.svg'
import ArrowRightOutlined from 'src/assets/icons/ico-arrow-right-outlined.svg'
import defaultFlyerImg from 'assets/zazzle-default-flyer.png'
import SettingIcon from 'assets/icons/setting-icon.png'

const getTicketTypes = ticketTypes => {
  return ticketTypes.map(item => ({
    id: uuid.v4(),
    label: item,
    price: 0
  }))
}

const EditEvent = props => {
  const dispatch = useDispatch()
  const {
    params: { eventToken }
  } = props

  const [loadingEvent, setLoadingEvent] = useState(true)
  const [child_count_enabled, setChild_count_enabled] = useState(false)
  const event = useSelector(eventSelector)

  const eventForm = useSelector(eventFormSelector)
  const userId = useSelector(userIdSelector)
  const { flyerList, customFlyerURL } = useSelector(flyerSelector)
  const ticketTypes = useSelector(ticketTypesSelector)

  const [selectedFlyerNumber, setSelectedFlyerNumber] = useState(null)
  const [isZoomFlyer, setIsZoomFlyer] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentView, setCurrentView] = useState('detail') // 'detail' | 'flyer'
  const [isOpenImageSearch, setIsOpenImageSearch] = useState(false)
  const flyerRef = useRef(null)
  const videoFlyerRef = useRef(null)
  const isEdit = Boolean(eventForm.id)
  const [isImageSearchLoading, setIsImageSearchLoading] = useState(false)
  const initialFlyerRef = useRef(null)
  const initialCustomFlyerURLRef = useRef(null)
  const [currentImageUrl, setCurrentImageUrl] = useState('')
  const [isLoadingEventInfo, setIsLoadingEventInfo] = useState(false)
  const [isDefaultFlyer, setIsDefaultFlyer] = useState(false)
  const [isLoadZazzleDesignImageSuccess, setIsLoadZazzleDesignImageSuccess] = useState(true)
  const [designId, setDesignId] = useState(null)
  const [blurredImageUrl, setBlurredImageUrl] = useState('')

  const handleSetTicketTypes = event => {
    dispatch(
      eventTicketInfoRequest({
        containerId: event.id,
        containerType: 'event',
        hostId: event.user_id,
        onSuccessCallback: res => {
          if (res?.data?.ticket_types_for_container) {
            let ticketTypes = deepCopy(res?.data.ticket_types_for_container)
            const adultTicketTypes = ticketTypes.filter(item => item.label === 'adult')
            const childTicketTypes = ticketTypes.filter(item => item.label === 'child')
            const otherTicketTypes = ticketTypes.filter(
              item => item.label !== 'adult' && item.label !== 'child'
            )
            const tempTicketTypes = adultTicketTypes
              .concat(childTicketTypes)
              .concat(otherTicketTypes)
            dispatch(setTicketTypes(tempTicketTypes))
          }
        }
      })
    )
  }

  useEffect(() => {
    let isMounted = true

    if (eventToken) {
      dispatch(
        fetchEvent(eventToken, async data => {
          if (!isMounted) return

          const { event } = data
          handleSetTicketTypes(event)
          const deepCopyEvent = deepCopy(event)
          deepCopyEvent.flyer_design_number = String(event.flyer.design_number)
          deepCopyEvent.flyer_design_slug = event.flyer.design_slug
          deepCopyEvent.starts_at = event.starts_at
            ? moment.tz(event.starts_at, event.timezone).format('YYYY-MM-DD HH:mm')
            : event.starts_at
          deepCopyEvent.ends_at = event.ends_at
            ? moment.tz(event.ends_at, event.timezone).format('YYYY-MM-DD HH:mm')
            : event.ends_at

          if (event.partner_data?.design_id) {
            setDesignId(event.partner_data.design_id)
          }

          dispatch(changeEventForm(deepCopyEvent))

          if (event.flyer?.image_bg_upload?.image_url) {
            if (event.flyer?.design_number === 0) {
              setIsDefaultFlyer(false)
              setCurrentImageUrl(event.flyer.image_bg_upload.image_url)
              setChild_count_enabled(event.child_count_enabled)
              setLoadingEvent(false)
            } else {
              const resp = await fetch(event.flyer?.image_bg_upload?.image_url)
              const imageBlob = await resp.blob()
              const reader = new FileReader()
              reader.onload = e => {
                dispatch(setCustomFlyerURL(e.target.result))
                setChild_count_enabled(event.child_count_enabled)
                setLoadingEvent(false)
              }
              reader.readAsDataURL(imageBlob)
            }
          } else {
            setChild_count_enabled(event.child_count_enabled)
            setLoadingEvent(false)
          }
        })
      )
    }

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    dispatch(fetchFlyerDesigns())

    return () => {
      dispatch(resetEventForm())
    }
  }, [])

  useEffect(() => {
    if (eventForm.flyer_design_number) {
      setSelectedFlyerNumber(eventForm.flyer_design_number)
    }
  }, [eventForm.flyer_design_number])

  useEffect(() => {
    if (currentImageUrl) {
      generate16to9ImageWithBlur(currentImageUrl).then(dataUrl => {
        setBlurredImageUrl(dataUrl)
      })
    }
  }, [currentImageUrl])

  const handleChangeFlyer = useCallback(() => {
    setCurrentView('detail')
    setSelectedFlyerNumber(eventForm.flyer_design_number)
  }, [eventForm.flyer_design_number])

  const handleOpenFlyerModal = useCallback(e => {
    e.stopPropagation()
    if (currentView === 'detail') {
      initialFlyerRef.current = {
        flyer_design_number: eventForm.flyer_design_number,
        flyer_design_slug: eventForm.flyer_design_slug,
        checkedFlyer: eventForm.checkedFlyer
      }

      initialCustomFlyerURLRef.current = customFlyerURL
    }
    setCurrentView('flyer')
  }, [currentView, eventForm.flyer_design_number, eventForm.flyer_design_slug, eventForm.checkedFlyer, customFlyerURL])

  const handleBackToDetail = () => {
    dispatch(changeEventForm({
      flyer_design_number: initialFlyerRef.current.flyer_design_number,
      flyer_design_slug: initialFlyerRef.current.flyer_design_slug,
      checkedFlyer: initialFlyerRef.current.checkedFlyer
    }))

    dispatch(setCustomFlyerURL(initialCustomFlyerURLRef.current))
    setSelectedFlyerNumber(initialFlyerRef.current.flyer_design_number)
    setTimeout(() => {
      setCurrentView('detail')
    }, 0)
  }

  const verifyTicketTypeChanged = () => {
    const isChanged = child_count_enabled !== eventForm.child_count_enabled
    if (!isEdit || !isChanged) {
      return
    }

    if (eventForm.child_count_enabled) {
      dispatch(createTicketType(eventForm.id))
    } else {
      const child = ticketTypes.find(item => item.label === 'child' && !item.archived_at)
      if (child) {
        dispatch(archiveTicketType(child.id))
      }
    }
  }

  const handleSubmit = (params = {}) => {
    if (userId) {
      window.mixpanel.track('Save CHANGES Clicked', {
        'flyer#': selectedFlyerNumber,
        user_id: userId
      })
      handleCreateOrEditEvent(params)
      return
    }

    const loginParams = {
      loginModalSubtitle: 'In order for you to create an Event, we need to confirm your number'
    }
    dispatch(showLogin(loginParams))
  }

  const handleCreateOrEditEvent = async ({ apple_place_id }) => {
    if (!selectedFlyerNumber) {
      message.error('Please select flyer.')
      setCurrentView('flyer')
      return
    }

    setIsLoading(true)
    const eventParams = deepCopy(eventForm)
    delete eventParams.checkedFlyer
    delete eventParams.invitations

    if (eventParams.starts_at) {
      eventParams.starts_at = moment.tz(eventParams.starts_at, eventParams.timezone).format()
      if (eventParams.ends_at) {
        eventParams.ends_at = moment.tz(eventParams.ends_at, eventParams.timezone).format()
      }
    }

    if (apple_place_id) {
      eventParams.apple_place_id = apple_place_id
    }

    if (!isEdit) {
      eventParams.ticket_types_attributes = getTicketTypes(
        eventForm.child_count_enabled ? ['adult', 'child'] : ['adult']
      )
    }

    dispatch(
      fetchCreateEvent({
        eventId: eventParams.id,
        eventForm: eventParams,
        onSuccessCallback: data => {
          const {
            event: { id, url }
          } = data

          verifyTicketTypeChanged()
          if (designId && selectedFlyerNumber === '0') {
            handleUploadFlyer(id, url, 'zazzle')
          } else if (eventForm.mediaType === 'video') {
            if (customFlyerURL) {
              handleUploadFlyer(id, url, eventForm.mediaType)
            } else {
              handleUploadVideoFlyer(id, url)
            }
          } else {
            handleUploadFlyer(id, url, eventForm.mediaType)
          }
        },
        onErrorCallback: () => {
          setIsLoading(false)
          message.error('Creation failed.')
        }
      })
    )
  }

  const handleUploadVideoFlyer = (eventId, eventUrl) => {
    dispatch(
      fetchUploadVideoFlyer({
        eventId,
        design_number: eventForm.flyer_design_number,
        imageBgFile: customFlyerURL,
        onSuccessCallback: async data => {
          const requests = []
          if (data?.video_flyer?.image_bg_upload?.presigned_url) {
            const imageBlob = await videoFlyerRef.current?.generateImage()
            if (!imageBlob) {
              alert('Upload Error.')
              setIsLoading(false)
            }
            requests.push(
              fetch(data.video_flyer.image_bg_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: imageBlob
              })
            )
          }

          Promise.all(requests).then(responses => {
            if (responses.every(response => response.status === 200)) {
              setTimeout(() => {
                redirectDomain(eventUrl + '/')
                dispatch(resetEventForm())
              }, 5000)
            } else {
              setIsLoading(false)
              alert('Upload Error.')
            }
          })
        },
        onErrorCallback: () => {
          setIsLoading(false)
          message.error('Creation failed.')
        }
      })
    )
  }

  const handleUploadFlyer = (eventId, eventUrl, mediaType) => {
    if (mediaType === 'zazzle') {
      dispatch(
        fetchUploadFlyer({
          eventId,
          design_number: 0,
          customBgDataURL: currentImageUrl,
          onSuccessCallback: async data => {
            const requests = []
            const flyerBgImageResponse = await fetch(blurredImageUrl)
            const flyerBgImageBlob = await flyerBgImageResponse.blob()
            if (data?.image_flyer?.image_upload?.presigned_url) {
              requests.push(
                fetch(data.image_flyer.image_upload.presigned_url, {
                  headers: {
                    'Content-Type': 'image/png'
                  },
                  method: 'PUT',
                  body: flyerBgImageBlob
                })
              )
            }

            if (data?.image_flyer?.image_bg_upload?.presigned_url) {
              requests.push(
                fetch(data.image_flyer.image_bg_upload.presigned_url, {
                  headers: {
                    'Content-Type': 'image/png'
                  },
                  method: 'PUT',
                  body: flyerBgImageBlob
                })
              )
            }

            if (requests.length === 0) {
              setIsLoading(false)
              alert('Upload Error.')
              return
            }

            Promise.all(requests).then(responses => {
              if (responses.every(response => response.status === 200)) {
                setTimeout(() => {
                  redirectDomain(eventUrl + '/')
                  dispatch(resetEventForm())
                }, 5000)
              } else {
                setIsLoading(false)
                alert('Upload Error.')
              }
            })
          }
        })
      )
      return
    }
    dispatch(
      fetchUploadFlyer({
        eventId,
        design_number: selectedFlyerNumber,
        customBgDataURL: customFlyerURL,
        onSuccessCallback: async data => {
          const requests = []
          if (data?.image_flyer?.image_upload?.presigned_url) {
            let flyerImageBlob
            if (mediaType === 'video') {
              flyerImageBlob = await videoFlyerRef.current?.generateImage()
            } else {
              flyerImageBlob = await flyerRef.current?.onCanvasToImage()
            }
            requests.push(
              fetch(data.image_flyer.image_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: flyerImageBlob
              })
            )
          }

          if (data?.image_flyer?.image_bg_upload?.presigned_url) {
            const flyerBgImage = await fetch(customFlyerURL)
            const flyerBgImageBlob = await flyerBgImage.blob()
            requests.push(
              fetch(data.image_flyer.image_bg_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: flyerBgImageBlob
              })
            )
          }

          if (requests.length === 0) {
            setIsLoading(false)
            alert('Upload Error.')
            return
          }

          Promise.all(requests).then(responses => {
            if (responses.every(response => response.status === 200)) {
              setTimeout(() => {
                redirectDomain(eventUrl + '/')
                dispatch(resetEventForm())
              }, 5000)
            } else {
              setIsLoading(false)
              alert('Upload Error.')
            }
          })
        }
      })
    )
  }

  const onClickRefresh = (e) => {
    e.stopPropagation()
    if (designId) {
      setIsLoadingEventInfo(true)

      dispatch(
        fetchZazzleEventInfo({
          design_id: designId,
          refresh: true,
          onSuccessCallback: data => {
            if (data?.design_file_url?.Front && data?.design_file_url?.Front !== currentImageUrl) {
              setCurrentImageUrl(data.design_file_url.Front)
              setIsDefaultFlyer(false)
            } else {
              setIsLoadingEventInfo(false)
            }
          },
          onErrorCallback: () => {
            setIsLoadingEventInfo(false)
          }
        })
      )
    }
  }

  const renderDesignActions = (showIcon, editButtonText = 'Edit') => {
    return (
      <div className={styles['design-actions']}>
        <Button className={styles['refresh-button']} onClick={onClickRefresh} disabled={isLoadingEventInfo || !designId}>
          {
            showIcon && (
              <SvgIcon className={styles['refresh-icon']} icon={refreshIcon} width="14" />
            )
          }
          <span>Refresh</span>
        </Button>

        <Button
          href={`${process.env.ZAZZLE_BASE_URL}/pd/spp/dz-${designId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles['edit-design-button']}
          wrapperElement="a"
          disabled={!designId}
          onClick={e => e.stopPropagation()}
        >
          <span>{editButtonText}</span>
          <SvgIcon className={styles['arrow-icon']} icon={ArrowRightOutlined} width="14" />
        </Button>
      </div>
    )
  }

  const handleLoadError = () => {
    setIsLoadZazzleDesignImageSuccess(false)
    setIsDefaultFlyer(true)
    setIsLoadingEventInfo(false)
  }

  const handleLoad = () => {
    setIsLoadZazzleDesignImageSuccess(true)
    setIsDefaultFlyer(false)
    setIsLoadingEventInfo(false)
  }

  const renderSelectedFlyer = () => {
    const customFlyerConfig = flyerDesignLayoutConfig.find(item => item.slug === 'custom')
    const isCustomFlyer = customFlyerConfig?.designIds.includes(Number(selectedFlyerNumber))
    const customFlyerDetail = { design_number: '8', design_slug: 'hobnob:custom' }

    const selectedFlyerDetail = isCustomFlyer
      ? customFlyerDetail
      : flyerList.find(flyer => flyer.design_number === selectedFlyerNumber)

    if (selectedFlyerDetail) {
      if (selectedFlyerDetail.video_bg_slug) {
        return (
          <VideoFlyerCard
            key={selectedFlyerDetail.design_number}
            ref={videoFlyerRef}
            flyerDetail={selectedFlyerDetail}
            isThumbnail
            showCheckIcon={false}
          />
        )
      }
      return (
        <FlyerCard
          key={selectedFlyerDetail.design_number}
          ref={flyerRef}
          flyerDetail={selectedFlyerDetail}
          isThumbnail
          showCheckIcon={false}
        />
      )
    } else if (selectedFlyerNumber === '0' && blurredImageUrl) {
      return (
        <div className={cn(styles['zazzle-flyer-card'], isDefaultFlyer && styles['default-flyer-card'])}>
          {isDefaultFlyer ? (
            <img
              src={defaultFlyerImg}
              alt="Default Flyer"
              className={styles['default-flyer-img']}
            />
          ) : (
            <div className={styles['flyer-img-wrapper']}>
              <img
                src={blurredImageUrl}
                alt="Flyer with blur background"
                onLoad={handleLoad}
                onError={handleLoadError}
                className={styles['flyer-img']}
              />
            </div>
          )}

          {isLoadingEventInfo && (
            <LoadingComponent
              loading
              dark
              content="Loading..."
              containerStyle={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2 }}
            />
          )}

          {
            isLoadZazzleDesignImageSuccess ? (
              <div className={styles['design-instructions']}>
                <div className={styles['instruction-text']}>
                  Go back to Zazzle to edit and click refresh to update
                </div>
                {renderDesignActions(
                  false,
                  'Edit'
                )}
              </div>
            ) : (
              <div className={styles['error-instructions']}>
                <div className={styles['error-overlay']}>
                  <span>It looks like we couldn't retrieve the design - please try again or go back to Zazzle to download the image to upload as a flyer</span>
                </div>
                {renderDesignActions(
                  true,
                  'Go Back to Zazzle'
                )}
              </div>
            )
          }
        </div>
      )
    } else if (!(isLoading || isImageSearchLoading) && !blurredImageUrl) {
      return (
        <div className={cn(styles['zazzle-flyer-card'], styles['loading-flyer-card'])}>
          <LoadingComponent
            loading
            dark
            content="Loading..."
            containerStyle={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2 }}
          />
        </div>
      )
    }
  }

  const getTitle = () => {
    return currentView === 'detail' ? 'Edit Event' : 'Design'
  }

  const handleClickBack = () => {
    if (currentView === 'detail') {
      history.back()
    } else if (currentView === 'flyer') {
      dispatch(changeEventForm({
        flyer_design_number: initialFlyerRef.current.flyer_design_number,
        flyer_design_slug: initialFlyerRef.current.flyer_design_slug,
        checkedFlyer: initialFlyerRef.current.checkedFlyer
      }))

      dispatch(setCustomFlyerURL(initialCustomFlyerURLRef.current))
      setSelectedFlyerNumber(initialFlyerRef.current.flyer_design_number)
      setTimeout(() => {
        setCurrentView('detail')
      }, 0)
    }
  }

  const renderHeaderLeft = () => {
    return (
      <>
        <img src={BackIcon} onClick={handleClickBack} width="12" />
      </>
    )
  }

  const renderRightButtons = () => {
    if (currentView === 'flyer') {
      return (
        <section className={styles['action-container']}>
          <div className={styles['action-item']}>
            <CustomFlyerPicker>
              <img src={ImagesIcon} width={'24px'} />
            </CustomFlyerPicker>

            <SvgIcon
              className={styles['search-icon']}
              icon={SearchIcon}
              onClick={() => setIsOpenImageSearch(true)}
              width={24}
            />
          </div>
        </section>
      )
    }
  }

  const handleImageSearchSave = async (url) => {
    setIsImageSearchLoading(true)

    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache'
      })

      if (response.ok) {
        const blob = await response.blob()
        const reader = new FileReader()

        reader.onload = (e) => {
          dispatch(setCustomFlyerURL(e.target.result))
          setIsOpenImageSearch(false)
          setIsImageSearchLoading(false)
        }

        reader.onerror = () => {
          throw new Error('Failed to read blob')
        }

        reader.readAsDataURL(blob)
        return
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (fetchError) {
      console.warn('Fetch failed, trying Image method:', fetchError)

      try {
        await loadImageWithCanvas(url)
        setIsOpenImageSearch(false)
        setIsImageSearchLoading(false)
      } catch (imageError) {
        console.error('Both methods failed:', imageError)
        message.error('Failed to load image. Please try a different image.')
        setIsOpenImageSearch(false)
        setIsImageSearchLoading(false)
      }
    }
  }

  const loadImageWithCanvas = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          canvas.width = img.naturalWidth
          canvas.height = img.naturalHeight
          ctx.drawImage(img, 0, 0)

          const dataURL = canvas.toDataURL('image/jpeg', 0.8)
          dispatch(setCustomFlyerURL(dataURL))
          resolve(dataURL)
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => {
        reject(new Error('Image load failed'))
      }

      img.src = url
    })
  }

  if (loadingEvent) {
    return <LoadingScreen event={event} />
  }

  return (
    <DashboardContainer
      title={getTitle()}
      left={renderHeaderLeft()}
      right={renderRightButtons()}
      width={88}
    >
      {currentView === 'detail' && (
        <div className={styles['input-all-options']}>
          <div
            className={cn(styles['flyer-container'], isZoomFlyer && styles['flyer-container-zoom'])}
            onClick={() => {
              if (isMobile()) {
                setIsZoomFlyer(!isZoomFlyer)
              }
            }}
          >
            {renderSelectedFlyer()}

            {
              selectedFlyerNumber !== '0' && (
                <img
                  onClick={handleOpenFlyerModal}
                  src={PreviewIcon}
                  width="54"
                  className={styles['preview-icon']}
                  alt="preview"
                />
              )
            }

          </div>

          {
            designId && selectedFlyerNumber === '0' ? (
              <InputOptions
                onSubmit={handleSubmit}
                customSettingNode={
                  <div className={styles['settings-button']}>
                    <img src={SettingIcon} width="24" />
                    Settings
                  </div>
                }
                showBackButton={false}
                saveButtonText="SAVE"
                warningText="Please make sure your Event Information matches your flyer"
                showTimeZoneReminder
                showCheckMarkIcon
              />
            ) : (
              <InputOptions onSubmit={handleSubmit} />
            )
          }

        </div>
      )}

      {currentView === 'flyer' && (
        <div className={styles['flyer-selection-container']}>
          <div className={cn(styles['back-button'])} onClick={handleBackToDetail}>
            <img src={BackIcon} width="12" />
          </div>

          <div className={styles['create-wrapper']}>
            <header className={styles['create-header-wrapper']}>
              <h1>Design</h1>
              <div
                className={styles['image-search-label-wrapper']}
                onClick={() => setIsOpenImageSearch(true)}
              >
                <SvgIcon className={styles['search-icon']} icon={SearchIcon} />
                <span>Image Search</span>
              </div>
            </header>
            <div className={styles['create-step-wrapper']}>
              <SelectFlyerStep onButtonClick={handleChangeFlyer} onClickBack={handleBackToDetail} type="edit" />
            </div>
          </div>
        </div>
      )}

      {
        isOpenImageSearch && currentView === 'flyer' && (
          <ImageSearchModal
            eventName={eventForm.name}
            show={isOpenImageSearch}
            handleCancel={() => setIsOpenImageSearch(false)}
            handleImageSearchSave={handleImageSearchSave}
          />
        )
      }

      <LoadingComponent loading={isLoading || isImageSearchLoading} dark />
    </DashboardContainer>
  )
}

EditEvent.propTypes = {
  params: PropTypes.object
}

export default EditEvent
